import Mention from '@tiptap/extension-mention'
import { PluginKey } from '@tiptap/pm/state'
import { ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react'
import { exitSuggestion, type SuggestionProps } from '@tiptap/suggestion'
import type { QueryClient } from '@tanstack/react-query'
import { portalApi } from '@services/api/portalApi'
import { ConversationStaffTag } from './components/ConversationStaffTag'
import { StaffMentionSuggestions, type StaffMentionItem, type MentionAttributes, type StaffMentionSuggestionsHandle } from './components/StaffMentionSuggestions'

const mentionKey = new PluginKey('conversationStaffMention')
const StaffMention = Mention.extend({ addNodeView() { return ReactNodeViewRenderer(ConversationStaffTag) } })

export function staffMentionExtension(client: QueryClient) {
    return StaffMention.configure({
        HTMLAttributes: { class: 'conversation-staff-tag' },
        renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        renderHTML: ({ node, options }) => ['span', options.HTMLAttributes, `@${node.attrs.label ?? node.attrs.id}`],
        suggestion: {
            pluginKey: mentionKey,
            char: '@', allowSpaces: true, debounce: 120,
            allow: ({ editor }) => editor.isFocused,
            items: async ({ query }) => {
                try {
                    const staff = await client.fetchQuery({
                        queryKey: ['conversations', 'mentionable-staff'], staleTime: 120000, retry: false,
                        queryFn: async ({ signal }) => (await portalApi.get<StaffMentionItem[]>('/conversations/mentionable-staff', { signal })).data,
                    })
                    const search = query.trim().toLocaleLowerCase()
                    return staff.filter(person => `${person.initials} ${person.name}`.toLocaleLowerCase().includes(search)).slice(0, 20)
                } catch { return [{ initials: '', name: 'Staff could not be loaded. Try typing @ again.', unavailable: true }] }
            },
            render: () => {
                let renderer: ReactRenderer<StaffMentionSuggestionsHandle, SuggestionProps<StaffMentionItem, MentionAttributes>> | null = null
                let unmount: (() => void) | undefined
                return {
                    onStart: props => {
                        renderer = new ReactRenderer(StaffMentionSuggestions, { editor: props.editor, props })
                        renderer.element.style.zIndex = '10001'
                        // Keep the React portal inside modal focus traps.
                        props.editor.view.dom.closest('[role="dialog"]')?.appendChild(renderer.element)
                        unmount = props.mount(renderer.element)
                    },
                    onUpdate: props => renderer?.updateProps(props),
                    onKeyDown: ({ event, view }) => {
                        if (event.key === 'Escape') { event.stopPropagation(); exitSuggestion(view, mentionKey); return true }
                        return renderer?.ref?.onKeyDown(event) ?? false
                    },
                    onExit: () => { unmount?.(); renderer?.destroy(); renderer = null },
                }
            },
        },
    })
}
